import { useCallback, useMemo } from 'react';
import { SelectOption } from '@bimone/bimtrackreactcomponents';
import { batch, useDispatch } from 'react-redux';
import { Hub } from '@bimone/bimtrack-core';
import { updateHub, updateProject } from '../../../redux/reducers/session';
import { useHubs } from '../../../hooks/useHubs';
import { useProjects } from '../../../hooks/useProjects';
import { useSession } from '../../../modules/session';
import { useNavigate } from 'react-router-dom';
import { useFetchHubs } from '../../../hooks/useFetchHubs';

export interface UseNavBarHubSelectorProps {
  isHubSelectionEnabled?: boolean;
  isNavBarRoutingEnabled?: boolean;
  isReady?: boolean;
  onSelection?: () => void;
  onSettled?: (hubs: Hub[]) => void;
}

export interface UseNavBarHubSelectorResult {
  states: {
    isLoading: boolean;
    hubOptions?: SelectOption<string>[];
    selectedHubOption?: SelectOption<string>;
  };
  actions: {
    handleOnSelectHubOption: (option: SelectOption<string>) => void;
  };
}

export function useNavBarHubSelector({
  isHubSelectionEnabled = true,
  isNavBarRoutingEnabled = false,
  isReady = true,
  onSelection,
  onSettled,
}: UseNavBarHubSelectorProps): UseNavBarHubSelectorResult {
  const session = useSession();
  const dispatch = useDispatch();
  const { isLoading } = useFetchHubs({ isHubSelectionEnabled, isReady, onSettled });
  const {
    states: { hubs },
  } = useHubs();
  const {
    actions: { updateProjects },
  } = useProjects();
  const navigate = useNavigate();

  const handleOnSelectHubOption = useCallback(
    (option: SelectOption<string>) => {
      if ((option.hub as Hub) !== session.hub) {
        onSelection?.();

        batch(() => {
          dispatch(updateHub(option.hub as Hub));
          dispatch(updateProjects(undefined));
          dispatch(updateProject(undefined));
        });

        if (isNavBarRoutingEnabled) {
          navigate(`/Hubs/${(option.hub as Hub).name}`);
        }
      }
    },
    [dispatch, isNavBarRoutingEnabled, navigate, onSelection, session.hub, updateProjects]
  );

  const hubOptions = useMemo(
    () =>
      hubs?.map((hub: Hub): SelectOption<string> => {
        return {
          value: hub.hashId,
          label: hub.name,
          hub,
        };
      }),
    [hubs]
  );

  const selectedHubOption = useMemo(
    () => hubOptions?.find(hub => hub.value === session.hub?.hashId),
    [hubOptions, session.hub?.hashId]
  );

  return {
    states: {
      isLoading,
      hubOptions,
      selectedHubOption,
    },
    actions: {
      handleOnSelectHubOption,
    },
  };
}
